import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Logo,
  MainBody,
  Title,
  Activity,
  Pitch,
  InfoTitle,
  InfoValue,
  ButtonWrapper,
  ViewProfileButton,
  ActionsWrapper,
  TopRow,
  BottomRow,
  LogoColumn,
  TopInfoColumn,
  TextCol,
  ButtonCol,
  InfoCol,
  Divider,
  ActionsButton,
  ActionsDropdown,
  ActionsDropdownButton,
  Titles,
  EcosystemBadge,
} from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { defaultPublicAdminLogo } from './behavior';

export interface TeiaPublicAdministrationCardProps {
  title?: string;
  pitch?: string;
  logo?: string;
  activity?: string;
  dirigenteMaximo?: string;
  numeroServidores?: string;
  areaInovacao?: string;
  state?: string;
  isInMyEcosystem?: boolean;
  badgeLabel?: string;
  isHidden?: boolean;
  onViewProfile?: () => void;
  onAddResident?: () => void;
  onRemoveResident?: () => void;
  onToggleHidden?: () => void;
}

export const TeiaPublicAdministrationCard = ({
  title,
  pitch,
  logo,
  activity,
  dirigenteMaximo,
  numeroServidores,
  areaInovacao,
  state: addressState,
  isInMyEcosystem = false,
  badgeLabel,
  isHidden = false,
  onViewProfile,
  onAddResident,
  onRemoveResident,
  onToggleHidden,
}: TeiaPublicAdministrationCardProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleActionsClick = () => setIsDropdownOpen((prev) => !prev);

  const showResidentActions =
    onAddResident != null && onRemoveResident != null;
  const showVisibilityToggle = onToggleHidden != null;
  const showActionsMenu = showResidentActions || showVisibilityToggle;

  const handleToggleHidden = () => {
    onToggleHidden?.();
    setIsDropdownOpen(false);
  };

  const handleAddOrRemoveResident = () => {
    if (isInMyEcosystem) onRemoveResident?.();
    else onAddResident?.();
    setIsDropdownOpen(false);
  };

  const handleCardClick = () => onViewProfile?.();

  return (
    <Card
      $isHidden={isHidden}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
    >
      <MainBody>
        <TopRow>
          <LogoColumn>
            <Logo src={logo || defaultPublicAdminLogo} alt={title || 'Logo'} />
          </LogoColumn>
          <TopInfoColumn>
            <Titles>
              <Title>{title || '-'}</Title>
              {badgeLabel != null && badgeLabel !== '' && (
                <EcosystemBadge>{badgeLabel}</EcosystemBadge>
              )}
            </Titles>
            <Activity>{activity || '-'}</Activity>
            <Pitch>{pitch || '-'}</Pitch>
          </TopInfoColumn>
        </TopRow>

        <Divider />

        <BottomRow>
          <TextCol>
            <InfoCol>
              <InfoTitle>Dirigente máximo</InfoTitle>
              <InfoValue>{dirigenteMaximo || '-'}</InfoValue>
            </InfoCol>
            <InfoCol>
              <InfoTitle>Número de servidores</InfoTitle>
              <InfoValue>{numeroServidores || '-'}</InfoValue>
            </InfoCol>
            <InfoCol>
              <InfoTitle>Área de inovação</InfoTitle>
              <InfoValue>{areaInovacao || '-'}</InfoValue>
            </InfoCol>
            <InfoCol>
              <InfoTitle>Estado</InfoTitle>
              <InfoValue>{addressState || '-'}</InfoValue>
            </InfoCol>
          </TextCol>

          <ButtonCol onClick={(e) => e.stopPropagation()}>
            <ActionsWrapper ref={dropdownRef}>
              <ButtonWrapper>
                <ViewProfileButton onClick={onViewProfile}>Ver perfil</ViewProfileButton>
                {showActionsMenu && (
                  <ActionsButton onClick={handleActionsClick}>
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </ActionsButton>
                )}
              </ButtonWrapper>
              {showActionsMenu && isDropdownOpen && (
                <ActionsDropdown>
                  {showVisibilityToggle && (
                    <ActionsDropdownButton onClick={handleToggleHidden}>
                      {isHidden ? 'Ativar órgão público' : 'Ocultar órgão público'}
                    </ActionsDropdownButton>
                  )}
                  {showResidentActions && (
                    <ActionsDropdownButton onClick={handleAddOrRemoveResident}>
                      {isInMyEcosystem ? 'Remover como residente' : 'Adicionar como residente'}
                    </ActionsDropdownButton>
                  )}
                </ActionsDropdown>
              )}
            </ActionsWrapper>
          </ButtonCol>
        </BottomRow>
      </MainBody>
    </Card>
  );
};
